<template>
  <div class="product-preview"
       :class="[
         getProductsDisplay,
         {
            'product-preview--quick-view': showQuickShopBtn
         }
       ]">
    <nuxt-link class="name"
               event
               :to="productPath"
               @click.native="onProductClicked">
      <div v-if="product.hover_image"
           :alt="product.hover_image_alternative_text"
           class="image-preview"
           @mouseover="setHovered(true)"
           @mouseleave="setHovered(false)">
        <button v-if="showQuickShopBtn"
                class="btn quick-shop-btn"
                @click.stop.prevent="onQuickShopClicked">
          QUICK VIEW
        </button>

        <product-sale-banner class="sale"
                             size="small"
                             :is-on-sale="product.on_sale"
                             :is-daily-deal="isDailyDeal"
                             :is-in-stock="product.in_stock"/>

        <transition name="fade">
          <div v-if="successChangeEnabled"
               class="product-changes-confirmed">
            <img src="@/assets/images/icons/success-check.svg"
                 class="product-changes-confirmed__icon"/>
            <span class="product-changes-confirmed__text">
              Changes confirmed
            </span>
          </div>
        </transition>

        <div v-if="isAdmin && enableEditAbility"
             class="edit-product-icon"
             @click.prevent.stop="showEditProductModal">
          <i class="fas fa-edit" />
        </div>

        <transition name="fade">
          <div v-if="hovered">
            <lazy-img :lazy-src="product.hover_image"
                      :alt="product.hover_image_alternative_text"
                      :fallback-image="$defaultVariantImage"
                      :key="!hovered"
                      loader
                      class="image-preview__image"/>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="!hovered">
            <lazy-img :lazy-src="product.main_image"
                      :alt="product.main_image_alternative_text"
                      :fallback-image="fallbackMainImage"
                      :class="horizontalFlip"
                      :key="hovered"
                      loader
                      class="image-preview__image"/>
          </div>
        </transition>
      </div>
      <div v-else class="image-preview">
        <button v-if="showQuickShopBtn"
                class="btn quick-shop-btn"
                @click.stop.prevent="onQuickShopClicked">
          QUICK VIEW
        </button>

        <product-sale-banner class="sale"
                             size="small"
                             :is-on-sale="product.on_sale"
                             :is-daily-deal="isDailyDeal"
                             :is-in-stock="product.in_stock"
                             :is-best-seller="product.top_seller"/>

        <transition name="fade">
          <div v-if="successChangeEnabled"
               class="product-changes-confirmed">
            <img src="../assets/images/icons/success-check.svg"
                 class="product-changes-confirmed__icon"/>
            <span class="product-changes-confirmed__text">
              Changes confirmed
            </span>
          </div>
        </transition>

        <div v-if="isAdmin && enableEditAbility"
             class="edit-product-icon"
             @click.prevent.stop="showEditProductModal">
          <i class="fas fa-edit" />
        </div>

        <lazy-img v-if="product.main_image"
                  :lazy-src="product.main_image"
                  :alt="product.main_image_alternative_text"
                  :fallback-image="fallbackMainImage"
                  :class="horizontalFlip"
                  loader
                  class="image-preview__image"/>
      </div>
    </nuxt-link>
    <div class="product-info">

      <nuxt-link class="name"
                 :class="{ 'no-transform': product.gift_card }"
                 :to="{ name: 'products-slug', query: buildQuery(), params: buildParams(product) }">
        {{ product.proper_name }}
      </nuxt-link>

      <div v-if="product.supplier && product.supplier.name"
           class="brand">
        <p>{{ product.supplier.name }}</p>
      </div>

      <div class="price"
           :class="{ sale: product.on_sale }">
        {{ formatPriceRange(product.price_show) }}
        <span v-if="product.on_sale"
              class="old"
              aria-label="Price before sale">
          {{ formatPriceRange(product.old_price_show) }}
        </span>

        <div v-if="product.free_shipping"
             class="free-shipping">
          ships free
        </div>
      </div>

      <div v-if="product.options_amount > 1"
           class="options-amount">
        <span>
          Available in {{ product.options_amount }} options
        </span>
      </div>
    </div>

    <favorite :favorableId="product.product_id"
              favorableType="Product"
              class="favorite"
              :size="20"/>

    <slot name="last"/>
  </div>

</template>

<script>
  import ApiProduct from '@/api/admin/Product'
  import LazyImg from '@/../shared/components/LazyImg'
  import Favorite from '@/components/Favorite'
  import ProductSaleBanner from '@/components/products/ProductSaleBanner'
  import WindowSizeMixin from '@/mixins/windowSize'
  import PriceMixin from '@/../shared/mixins/price'
  import { pickBy } from 'lodash'
  import { mapGetters } from 'vuex'

  export default {
    name: 'product-preview',
    mixins: [WindowSizeMixin, PriceMixin],
    data: function() {
      return {
        hovered: false,
        successChangeEnabled: false,
        properQueryId: this.$route.query.queryId || this.queryId
      }
    },
    props: {
      product: {
        type: Object,
        required: true
      },
      preventDefault: {
        type: Boolean,
        default: false
      },
      enableEditAbility: {
        type: Boolean,
        default: false
      },
      categories: {
        type: Array,
        default: () => []
      },
      showQuickShopBtn: {
        type: Boolean,
        default: false
      },
      queryId: {
        type: String,
        default: null
      }
    },
    components: {
      Favorite,
      LazyImg,
      ProductSaleBanner
    },
    methods: {
      onQuickShopClicked () {
        this.$emit('showProduct', { ...this.buildParams(this.product), ...this.buildQuery() })
        this.$emit('productClicked')
      },
      showEditProductModal () {
        this.$store.dispatch('modal/showModal', {
          component: 'EditProductModal',
          classes: ['auto-width'],
          props: {
            product: this.product,
            categories: this.categories,
            confirmFunction: (modal) => {
              modal.errors = {}
              modal.loading = true
              ApiProduct.shopEditProduct(this.$axios, this.product.slug, modal.form)
                .then(() => {
                  this.product = { ...this.product, ...modal.form }
                  this.showSuccessChanges()
                  this.$store.dispatch('modal/hideModal')
                })
                .catch(error => {
                  modal.errors = error.response.data
                })
                .finally(() => {
                  modal.loading = false
                })
            }
          }
        })
      },
      showSuccessChanges () {
        this.successChangeEnabled = true
        setTimeout(() => {
          this.successChangeEnabled = false
        }, 5000)
      },
      setHovered(status) {
        this.hovered = status
      },
      freeShipping(product) {
        return product.free_shipping
      },
      displayPrice() {
        return formattedPrice
      },
      displayOldPrice() {
        return formattedPrice
      },
      buildParams(product) {
        return pickBy({
          slug: product.slug || product.variant_display.slug,
          listPageMetadata: this.$route
        })
      },
      buildQuery () {
        return pickBy({ queryId: this.properQueryId })
      },
      onProductClicked () {
        this.$emit('productClicked')

        if (!this.preventDefault) {
          this.$router.push(this.productPath)
        } else {
          this.$emit('showProduct', { ...this.buildParams(this.product), ...this.buildQuery() })
        }
      }
    },
    computed: {
      ...mapGetters('globalData', ['getLayout']),
      horizontalFlip () {
        return { 'horizontal-flip': this.product.main_image_flipped }
      },
      fallbackMainImage () {
        return this.product.main_image_flipped ? this.$defaultVariantImageFlipped : this.$defaultVariantImage
      },
      getProductsDisplay() {
        return this.$store.getters['globalData/getProductsDisplay']
      },
      itemsPerRow () {
        return this.isMobile ? 2 : 4
      },
      productPath () {
        return {
          name: 'products-slug',
          query: this.buildQuery(),
          params: this.buildParams(this.product)
        }
      },
      isAdmin () {
        return this.$store.getters['auth/signedAdmin']
      },
      isDailyDeal () {
        return this.getLayout === 'cyberWeek' && this.product.properties.filter_feature?.includes('Daily Deals')
      }
    }
  }

</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/variables.scss';
  @import '@/assets/stylesheets/animations/fade';

  .quick-shop-btn {
    position: absolute;
    width: fit-content;
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
    display: none;
    background: transparent;
    border: 1px solid $color-white;
    color: $color-white;
    font-family: 'MotivaSans';
    font-size: 1.4rem;
    z-index: 2;
    padding: 10px 40px;

    &:hover {
      background: $color-white;
      color: $color-black;
    }
  }

  .horizontal-flip {
    transform: scaleX(-1);
  }

  .product-preview {
    position: relative;
    display: grid;
    align-content: baseline;
    row-gap: 14px;
    grid-template-columns: 1fr;
    font-family: 'MotivaSans';

    &--quick-view:hover {
      .image-preview {
        &:before {
          content: '';
          background: $color-black;
          opacity: .5;
          width: 100%;
          height: 100%;
          position: absolute;
          z-index: 1;
        }
      }

      .quick-shop-btn {
        display: block;
      }

      .name {
        font-family: 'MotivaSans SemiBold' !important;
        color: $dark-chestnut-brown !important;
      }
    }

    .crossed-out {
      text-decoration: line-through;
      font-family: 'MotivaSans Light';
    }

    .favorite{
      position: absolute;
      top: 3px;
      right: 3px;

      ::v-deep .fa-stack {
         top: 14px;
      }
    }

    .free-shipping {
      color: $rose-gold;
      font-size: 1.2rem;
      line-height: 1.44rem;
      font-style: italic;
      display: inline-block;
      font-family: 'MotivaSans Light' !important;
    }

    .options-amount {
      margin-top: 10px;
      font-family: 'MotivaSans Light';
      font-size: 1.2rem;
      line-height: 1.44rem;
      color: $dark-silver;
      text-transform: uppercase;
    }

    .image-preview{
      position: relative;
      height: 300px;

      &__image {
        max-height: 300px;
        width: auto;
        max-width: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
      }

      .sale {
        position: absolute;
        top: -9px;
        left: -9px;
        z-index: 15;
       }

      .product-changes-confirmed {
        padding: 10px 0;
        text-align: center;
        position: absolute;
        z-index: 16;
        background: $color-white;
        border: 2px solid $success-color;
        box-sizing: border-box;
        border-radius: 2px;
        width: 100%;

        &__icon {
          margin-right: 4px;
        }

        &__text {
          vertical-align: top;
          color: $success-color;
          font-family: OpenSans;
          font-style: normal;
          font-weight: bold;
          font-size: 16px;
          line-height: 22px;
          text-transform: uppercase;
        }
      }

      .edit-product-icon {
        color: $rose-gold;
        font-size: 2.5rem;
        position: absolute;
        z-index: 15;
        right: 0;
        padding: 0.33rem 0.66rem;
      }

      .edit-product-icon:hover {
        cursor: pointer;
      }
    }

    .product-info{
      .brand {
        p {
          font-family: 'MotivaSans Light';
          margin: 5px 0;
          letter-spacing: 1.4px;
          line-height: 14.4px;
          font-size: 1.2rem;
          color: $color-black;
          text-transform: uppercase;
        }
      }

      .name {
        font-family: 'MotivaSans Light';
        margin-top: 5px 0;
        font-size: 1.5rem;
        color: $gunmetal;
        overflow: hidden;
        text-decoration: none;
        text-transform: capitalize;
      }

      .price {
        margin-top: 15px;
        font-size: 1.4rem;
        padding-top: 0px;
        color: $gunmetal;
        font-family: 'MotivaSans SemiBold';
        column-gap: 7px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        &.sale {
          color: $venetian-red;
        }

        .old {
          color: $gunmetal;
          font-family: 'MotivaSans Light';
          text-decoration: line-through;
        }
      }

      .saleFH {
        font-family: 'OpenSans Bold';
        font-size: 1.3rem;
        color: $rust-orange;
        letter-spacing: 1.4px;
        font-weight: normal;
        margin-bottom: 0px;
        margin-top: 5px;
        span {
          text-transform: uppercase;
        }
      }
    }

    &.list{
      grid-template-columns: 200px 1fr;
      grid-column-gap: 10px;

      .image-preview{
        height: 200px;

        img{
          max-height: 200px;
        }
      }
    }

    @media screen and (max-width: 992px) {
      .price {
        max-width: 175px;
      }
    }

    @media only screen and (max-width: 600px) {
      &.list{
        grid-template-columns: 1fr;
      }

      .image-preview{
        height: 150px;

        img{
          max-height: 150px;
        }
      }
    }


    @media only screen and (min-width: 600px) and (max-width: 768px) {
      &.list{
        grid-template-columns: 1fr;
      }

      .image-preview{
        height: 200px;

        img{
          max-height: 200px;
        }
      }
    }

    @media only screen and (min-width: 768px) and (max-width: 992px) {
      &.list{
        grid-template-columns: 1fr;
      }

      .image-preview{
        height: 250px;

        img{
          max-height: 250px;
        }
      }
    }
  }
</style>
